import { startTransition, useEffect, useState } from "react";
import { classMerge } from "./classMerge";
import { isString } from "./getFromDataPath";
import { EditorDefns, EditorProps } from "./interface";

/**
 * String editor component for testing purposes
 *
 * @param props - The component props for the test editor
 */
export const DefaultEditor = <T,>(props: EditorProps<T>) => {
  const [text, setText] = useState<string>();
  useEffect(() => {
    /* istanbul ignore else */
    if (typeof props.value === "string") startTransition(() => setText(props.value as string));
  }, [props.value]);

  if (typeof props.value !== "string")
    throw new Error("If newText is not a string, a custom function is required");

  const textAlign =
    props.textAlignment === "decimal"
      ? "center"
      : props.textAlignment === "default"
        ? "left"
        : (props.textAlignment ?? "left");

  const inputProps: React.InputHTMLAttributes<HTMLInputElement> &
    React.TextareaHTMLAttributes<HTMLTextAreaElement> = {
    "aria-label": props["aria-label"] as string | undefined,
    "aria-labelledby": props["aria-labelledby"] as string | undefined,
    className: classMerge(["test-editor", props.className]),
    disabled: !props.setValue || !props.editable,
    id: props.id,
    onBlur: () => props.setValue && props.setValue(text as T),
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
      setText(e.currentTarget.value),
    style: {
      background: "transparent",
      border: "none",
      boxSizing: "border-box",
      padding: "4px 8px",
      textAlign,
      width: "100%",
    },
    value: text ?? "",
  };

  return props.resize ? (
    <textarea
      {...inputProps}
      data-testid={`test-editor-${props.id}`}
    />
  ) : (
    <input
      {...inputProps}
      data-testid={`test-editor-${props.id}`}
    />
  );
};

DefaultEditor.displayName = "DefaultEditor";

/**
 * Check if the text in the test editor is a string
 *
 * @param o - The value to check
 */
const checkTextInDefaultEditor = (o: unknown) => typeof o === "string";

/**
 * Get text from the editor
 *
 * @param text - The text content to extract from the default editor
 */
const getTextFromDefaultEditor = <T,>(text: T) => {
  if (!checkTextInDefaultEditor(text)) {
    throw new Error("Update function when using an object for text");
  }
  return [text];
};

/**
 * Replace text in the editor
 *
 * @param s - The source string to perform replacement on
 * @param oldPhrase - The phrase to find and replace
 * @param newPhrase - The phrase to replace with
 */
const replaceTextInDefaultEditor = <T,>(s: T, oldPhrase: string, newPhrase: T) => {
  if (!checkTextInDefaultEditor(s) || !checkTextInDefaultEditor(newPhrase)) {
    throw new Error("Update function when using an object for text");
  }
  return s.replace(oldPhrase, newPhrase as string);
};

/**
 * Join lines into a block of text
 *
 * @param lines - The array of text lines to process
 */
const joinDefaultEditorIntoBlock = <T,>(lines: T[]) => {
  if (lines.some((line) => !checkTextInDefaultEditor(line))) {
    throw new Error("Update function when using an object for text");
  }
  return lines.join("\n");
};

/**
 * Split text into lines
 *
 * @param text - The text content to split into lines
 */
const splitDefaultEditorIntoLines = <T,>(text: T) => {
  if (!checkTextInDefaultEditor(text)) {
    throw new Error("Update function when using an object for text");
  }
  return text.split("\n");
};

export const defaultEditorDefns: EditorDefns<unknown> = {
  Editor: DefaultEditor,
  blankT: "",
  getTextFromT: getTextFromDefaultEditor,
  isT: isString,
  joinTintoBlock: joinDefaultEditorIntoBlock,
  replaceTextInT: replaceTextInDefaultEditor,
  splitTintoLines: splitDefaultEditorIntoLines,
  textToT: (text: string) => text,
};

/**
 * String-specialised default editor definitions for table and scaffold tests.
 */
export const defaultStringEditorDefns: EditorDefns<string> =
  defaultEditorDefns as EditorDefns<string>;
